import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import { TrendingUp, DollarSign, Send, CheckCircle2 } from "lucide-react";

const RANGES = [{ key: "7d", label: "7 days" }, { key: "30d", label: "30 days" }, { key: "90d", label: "90 days" }];
const COLORS = ["#2563eb", "#d97706", "#059669", "#8b5cf6", "#0284c7", "#dc2626", "#94a3b8", "#f59e0b"];

function Funnel({ label, value }) {
  return (
    <Card className="p-5 border-slate-200">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="text-3xl font-bold font-heading mt-1 text-blue-600">{value}%</p>
    </Card>
  );
}

export default function Analytics() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => { setData(null); api.get(`/analytics?range=${range}`).then(({ data }) => setData(data)); }, [range]);

  if (!data) return (
    <div className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading">Analytics</h1>
      <div className="grid md:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      <Skeleton className="h-80 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">Analytics</h1>
          <p className="text-slate-500 mt-1">Understand what's driving your business.</p>
        </div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="analytics-range">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`analytics-range-${r.key}`} className={`px-3 py-1.5 text-sm rounded-md font-medium ${range === r.key ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{r.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 border-slate-200" data-testid="analytics-total-leads"><div className="flex items-center gap-2 text-slate-500 text-sm"><TrendingUp className="h-4 w-4" /> Total leads</div><p className="text-3xl font-bold font-heading mt-1">{data.total_leads}</p></Card>
        <Card className="p-5 border-slate-200" data-testid="analytics-opportunity"><div className="flex items-center gap-2 text-slate-500 text-sm"><DollarSign className="h-4 w-4" /> Opportunity value</div><p className="text-3xl font-bold font-heading mt-1">${Number(data.opportunity_value).toLocaleString()}</p></Card>
        <Card className="p-5 border-slate-200" data-testid="analytics-sent"><div className="flex items-center gap-2 text-slate-500 text-sm"><Send className="h-4 w-4" /> Follow-ups sent</div><p className="text-3xl font-bold font-heading mt-1">{data.follow_ups_sent}</p></Card>
        <Card className="p-5 border-slate-200" data-testid="analytics-jobs"><div className="flex items-center gap-2 text-slate-500 text-sm"><CheckCircle2 className="h-4 w-4" /> Jobs completed</div><p className="text-3xl font-bold font-heading mt-1">{data.jobs_completed}</p></Card>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Funnel label="Lead → Contact" value={data.lead_to_contact} />
        <Funnel label="Lead → Appointment" value={data.lead_to_appointment} />
        <Funnel label="Appointment → Job" value={data.appointment_to_job} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200">
          <h3 className="font-heading font-semibold text-lg mb-4">Leads by source</h3>
          {data.by_source.length === 0 ? <p className="text-slate-400 text-sm py-12 text-center">No lead data yet.</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.by_source} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={90} label>
                  {data.by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6 border-slate-200">
          <h3 className="font-heading font-semibold text-lg mb-4">Monthly lead volume</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.monthly_volume}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip />
              <Line type="monotone" dataKey="leads" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
